import { bulkPutCards, bulkPutPacks, clearContent, setMeta } from ".";
import type { Card, Pack } from "../domain/types";
import { isApiMode } from "../lib/apiMode";

const LS_VERSION_KEY = "pp.contentVersion";

interface ContentManifest {
  packs: Pack[];
  cards: Card[];
  /** A monotonic integer — bumping it triggers a re-import. */
  version: number;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
  return res.json() as Promise<T>;
}

/**
 * Resolve a content URL using Vite's BASE_URL so the fetch path stays correct
 * regardless of which route the user is currently on. Without this, calling
 * `fetch("./content/packs.json")` from `/players/friends-1` resolves to
 * `/players/content/packs.json` and falls through to the SPA index.html →
 * JSON.parse explodes on "<!doctype ...".
 */
function contentUrl(name: string): string {
  const base = import.meta.env.BASE_URL || "/";
  const trimmed = base.endsWith("/") ? base : base + "/";
  return `${trimmed}content/${name}`;
}

/** API mode source. {packs, cards, contentVersion} comes from /api/content. */
interface ApiContentPayload {
  contentVersion: number;
  packs: Pack[];
  cards: Card[];
}

async function fetchContentBundle(): Promise<{
  packs: Pack[];
  cards: Card[];
  version: number;
}> {
  if (isApiMode()) {
    // API mode — single round-trip + server-supplied version.
    // Resilience: if the backend is unreachable (server crashed / not yet
    // started), fall back to static JSON so the game keeps playing on the
    // bundled content rather than showing an empty screen.
    try {
      const payload = await fetchJson<ApiContentPayload>("/api/content");
      return {
        packs: payload.packs,
        cards: payload.cards,
        version: payload.contentVersion,
      };
    } catch (err) {
      console.warn(
        "[seed] API mode but /api/content unreachable — falling back to static JSON.",
        err,
      );
      // fall through to static fetch below
    }
  }
  // Static mode (default) OR API-mode fallback. Version from pack.version sum.
  const packs = await fetchJson<Pack[]>(contentUrl("packs.json"));
  const cards = await fetchJson<Card[]>(contentUrl("cards.json"));
  return { packs, cards, version: computeVersion(packs) };
}

/**
 * Hydrate IndexedDB from static JSON or API on first load and on version bump.
 * Subsequent loads are a fast no-op (single localStorage read).
 */
let inFlight: Promise<void> | null = null;
export function ensureContent(force = false): Promise<void> {
  // Singleton guard — main.tsx fires ensureContent at boot AND Splash fires it
  // again on mount. Without this lock, two parallel bulkPut transactions on
  // the same IDB connection can deadlock waiting on each other.
  if (inFlight && !force) return inFlight;
  inFlight = doEnsureContent(force).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doEnsureContent(force: boolean): Promise<void> {
  // Source-agnostic fetch — branches on VITE_USE_API internally.
  const { packs, cards, version: manifestVersion } = await fetchContentBundle();

  const stored = readStoredVersion();
  if (!force && stored === manifestVersion) {
    return;
  }

  // totalCards is resolved from card count per pack/language at import time.
  const cardsPerPack = countCardsPerPack(cards);
  const enrichedPacks: Pack[] = packs.map((p) => ({
    ...p,
    totalCards: cardsPerPack.get(p.id) ?? 0,
    // status/progressPct fields are runtime — reset to neutral on import.
    status: "not_started",
    progressPct: 0,
  }));

  await clearContent();
  await bulkPutPacks(enrichedPacks);
  await bulkPutCards(cards);
  await setMeta("imported_at", Date.now());
  writeStoredVersion(manifestVersion);
}

/** A best-effort version derived from pack `version` fields, so packs.json alone drives imports. */
function computeVersion(packs: Pack[]): number {
  return packs.reduce((acc, p) => acc + (p.version || 0), packs.length);
}

function countCardsPerPack(cards: Card[]): Map<string, number> {
  // §2: one Card row per language. totalCards is per-language count, but since
  // every card has all 3 languages, dividing by 3 gives the gameplay length.
  // We use the per-language count as the canonical totalCards (matches Android).
  const perLang = new Map<string, number>();
  for (const c of cards) {
    if (c.language === "uk") {
      perLang.set(c.packId, (perLang.get(c.packId) ?? 0) + 1);
    }
  }
  return perLang;
}

function readStoredVersion(): number | null {
  try {
    const v = localStorage.getItem(LS_VERSION_KEY);
    return v == null ? null : Number(v);
  } catch {
    return null;
  }
}
function writeStoredVersion(v: number): void {
  try {
    localStorage.setItem(LS_VERSION_KEY, String(v));
  } catch {
    /* ignore */
  }
}

/** Used by Settings → Reset content. Wipes cards/packs only — sessions intact. */
export async function resetContent(): Promise<void> {
  await ensureContent(true);
}

export type { ContentManifest };
